import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, defer, EMPTY, Subscription } from 'rxjs';
import { PaginatedResult, Pagination, PaginationStore } from '../../../../../shared/store/pagination.store';
import { arr_map, blank, filled, observe, spf, when } from '../../../../../shared/utils/common';
import { ChecklistResponseLinkedAsset } from '../../../../../services/checklist.service';
import { memoize } from 'lodash-es';
import { RenderPromptsButtonCompleteEvent } from '../../dialogs/render-prompts/render-prompts.component';
import { ApiService } from '../../../../../services/api.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'checklist-responses-assets-list',
  template: `
    <fieldmagic-pagination-links
      [hideRefresh]="true"
      [topOnly]="true"
    >
      <table class="table table-bordered">
        <thead>
          <tr>
            <td width="20%">
              <fieldmagic-text
                content="Asset"
                purpose="table-header"
              >
              </fieldmagic-text>
            </td>
            <td width="10%">
              <fieldmagic-text
                content="Status"
                purpose="table-header"
              >
              </fieldmagic-text>
            </td>
            <td width="5%"></td>
          </tr>
        </thead>
        <tbody *ngIf="list$ | async as assets">
          <tr
            *ngIf="assets | blank"
          >
            <td
              class="text-center"
              colspan="3"
            >
              No linked assets.
            </td>
          </tr>

          <tr
            *ngFor="let asset of assets; let asset_index = index"
            [class.row-asset-pending]="asset.inspection_status == 'awaiting_completion'"
            [class.row-asset-passed]="asset.inspection_status == 'pass'"
            [class.row-asset-failed]="asset.inspection_status == 'fail'"
          >
            <td>
              <a
                [routerLink]="['/assets', asset.id]"
                target="_blank"
                matTooltip="Click to view asset"
              >
                {{ asset.text }}
              </a>
            </td>

            <td>
              {{ asset.inspection_status | translate }}
            </td>

            <td>
              <div class="w-100 d-flex justify-content-center">
                <checklist-render-prompts-button
                  [options]="{
                    job_id: asset.job_id,
                    asset_id: asset.id,
                    asset_type_id: asset.asset_type_id,
                    module_name: 'jobs',
                    questions_name: 'per_asset_prompts',
                    asset_job_id: asset.asset_job_id,
                    asset_type_variant_id: asset.asset_type_variant_id
                  }"
                  (complete)="onComplete($event, asset_index)"
                >
                </checklist-render-prompts-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </fieldmagic-pagination-links>
  `,
  styles: [
    `.row-asset-pending {
      background : #f8c77e;
    }`,
    `.row-asset-failed {
      background: #ffd9d9;
    }`,
    `.row-asset-passed {
      background: #e0f4d2;
    }`
  ],
  providers: [
    PaginationStore,
  ],
})
export class ChecklistResponseAssetsListComponent implements OnInit, OnDestroy {
  readonly list$ = new BehaviorSubject<DisplayedChecklistResponseLinkedAsset[]>([]);

  readonly _assets$ = new BehaviorSubject<ChecklistResponseLinkedAsset[]>([]);

  @Input()
  set assets(value: ChecklistResponseLinkedAsset[]) {
    this._assets$.next(value);
  }

  private _onPageChangeSubscription?: Subscription;
  private _onPageListChangeSubscription?: Subscription;
  private _onChecklistResponseSavedSubscription?: Subscription;

  constructor(
    private readonly _pagination: PaginationStore<DisplayedChecklistResponseLinkedAsset, number>,
    private readonly _api: ApiService,
  ) { }

  ngOnInit(): void {
    this._onPageListChangeSubscription = this._pagination.list$.subscribe((list) => this.list$.next(list));

    this._onPageChangeSubscription = this._pagination.onNextPage$.subscribe((page) => {
      if (blank(page)) {
        page = 0;
      }

      const assets = this._assets$.value;

      if (assets.length < 1) {
        return this._pagination.setCurrent(new PaginatedResult({
          items: [],
          pagination: new Pagination({
            next_page: null,
          }),
        }));
      }

      const max = Math.ceil(assets.length / 10);
      const items = arr_map(assets.slice(page * 10, ((page + 1) * 10)), {
        transformer: (item) => ({
          ...item,
          text: this._computeDisplayedText(item),
        })
      });

      const next = page + 1;

      this._pagination.setCurrent(new PaginatedResult({
        items: items,
        pagination: new Pagination({
          next_page: when(next < max, {
            then: () => next,
            else: () => null,
          }),
          total: items.length,
        }),
      }));
    });
  }

  ngOnDestroy(): void {
    this._pagination.reset();

    if (this._onPageChangeSubscription) {
      this._onPageChangeSubscription.unsubscribe();
    }

    if (this._onPageListChangeSubscription) {
      this._onPageListChangeSubscription.unsubscribe();
    }

    if (this._onChecklistResponseSavedSubscription) {
      this._onChecklistResponseSavedSubscription.unsubscribe();
    }
  }

  onComplete = (saved: RenderPromptsButtonCompleteEvent, index: number) => observe({
    observable: () => defer(() => {
      if (! saved) {
        return EMPTY;
      }

      const asset = this.list$.value[index];

      return this._api.callPrivate$({
        action: 'post',
        path: '/checklists/get_inspection_status',
        expectsNewFormat: true,
        body: {
          'asset_job_id': asset.asset_job_id
        },
        onSuccessResponse: (response) => response.get('status'),
      })
    }),
  }).pipe(
    filter(filled)
  ).subscribe((status: string) => {
    const assets = this.list$.value;

    assets[index] = {
      ... assets[index],
      inspection_status: status,
    };

    this.list$.next(assets);
  });

  private _computeDisplayedText = memoize((asset: ChecklistResponseLinkedAsset): string => {
    const name = asset.name;
    const serial = asset.serial_number;

    if (name == serial || blank(name)) {
      return spf('#%s', {
        args: [serial],
      });
    }

    return spf('%s (#%s)', {
      args: [
        name,
        serial,
      ],
    });
  });
}

type DisplayedChecklistResponseLinkedAsset = ChecklistResponseLinkedAsset & {
  text: string;
}